export interface WebSocketEvent {
  type: string;
  id: string;
  externalId: string;
  status: string;
  amountCents?: number | null;
  createdAt: string;
  operation: "insert" | "update" | "delete" | string;
  [key: string]: unknown;
}

export interface WebSocketCallbacks {
  onEvent?: (event: WebSocketEvent) => void;
  onError?: (error: Event | Error) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export function createWebSocketConnection(callbacks: WebSocketCallbacks): WebSocket | null {
  const baseWsUrl = import.meta.env.VITE_WS_URL;

  try {
    const token = localStorage.getItem("auth_token");

    const url = new URL(baseWsUrl);
    if (token) {
      url.searchParams.append("token", token);
    }

    const wsUrl = url.toString();

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      callbacks.onOpen?.();
    };

    ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data) as WebSocketEvent;
        callbacks.onEvent?.(data);
      } catch (error) {
        if (import.meta.env.DEV) {
        console.error("[WebSocket] Failed to parse message:", error);
        }
        callbacks.onError?.(error as Error);
      }
    };

    ws.onerror = (error) => {
      const token = localStorage.getItem("auth_token");
      if (token || ws.readyState !== WebSocket.CONNECTING) {
        if (import.meta.env.DEV) {
        console.error("[WebSocket] Error:", error);
        }
      }
      callbacks.onError?.(error);
    };

    ws.onclose = (event) => {
      if (import.meta.env.DEV && event.code !== 1000) {
        console.warn("[WebSocket] Unexpected close code:", event.code, "Reason:", event.reason);
      }

      callbacks.onClose?.();
    };

    return ws;
  } catch (error) {
    if (import.meta.env.DEV) {
    console.error("[WebSocket] Failed to create connection:", error);
    }
    callbacks.onError?.(error as Error);
    return null;
  }
}

interface ReconnectOptions {
  initialDelay?: number;
  maxDelay?: number;
  maxAttempts?: number;
}

export function createWebSocketConnectionWithReconnect(
  callbacks: WebSocketCallbacks,
  options: ReconnectOptions = {}
): {
  ws: WebSocket | null;
  reconnect: () => void;
  disconnect: () => void;
} {
  const { initialDelay = 1000, maxDelay = 30000, maxAttempts = 10 } = options;

  let ws: WebSocket | null = null;
  let reconnectTimeout: NodeJS.Timeout | null = null;
  let shouldReconnect = true;
  let isFirstAttempt = true;
  let attemptCount = 0;

  const getNextDelay = () => {
    const delay = Math.min(initialDelay * Math.pow(2, attemptCount), maxDelay);
    const jitter = delay * 0.2 * (Math.random() * 2 - 1);
    return Math.round(delay + jitter);
  };

  const connect = () => {
    if (!shouldReconnect) return;

    if (attemptCount >= maxAttempts) {
      if (import.meta.env.DEV) {
        console.warn(`[WebSocket] Max reconnect attempts (${maxAttempts}) reached. Giving up.`);
      }
      return;
    }

    const token = localStorage.getItem("auth_token");
    if (!token && isFirstAttempt) {
      reconnectTimeout = setTimeout(() => {
        isFirstAttempt = false;
        connect();
      }, 1000);
      return;
    }

    isFirstAttempt = false;

    ws = createWebSocketConnection({
      ...callbacks,
      onOpen: () => {
        attemptCount = 0;
        callbacks.onOpen?.();
      },
      onClose: () => {
        callbacks.onClose?.();
        if (shouldReconnect) {
          attemptCount++;
          const delay = getNextDelay();
          if (import.meta.env.DEV) {
            console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${attemptCount}/${maxAttempts})`);
          }
          reconnectTimeout = setTimeout(() => {
            connect();
          }, delay);
        }
      },
      onError: (error) => {
        callbacks.onError?.(error);
      },
    });
  };

  const disconnect = () => {
    shouldReconnect = false;
    attemptCount = 0;
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    if (ws) {
      ws.close();
      ws = null;
    }
  };

  const reconnect = () => {
    disconnect();
    shouldReconnect = true;
    attemptCount = 0;
    connect();
  };

  connect();

  return { ws, reconnect, disconnect };
}
